import React from 'react';
import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';
import { Checkbox } from '../form/checkbox';
import { PicSelectionOptions } from '../pic/pic.Selector';
import { ColorsSwatch } from '../form/colorsSwatch';
import { FitOptionsButtons, MaskControls } from '../form/mask.Controls';
import {
  SettingsRow,
  SettingsSelect,
  WidgetSettingsCollapsibleSection,
} from '../form/settings.Widget';

export const DEFAULT_LINK_DISPLAY_OPTIONS = [
  { type: 'label', checked: true, position: 'bas' },
  { type: 'secondary', entries: [] },
  {
    type: 'avatar',
    checked: true,
    maskPicIndex: null,
    maskFit: 'contain',
    maskInvert: false,
    maskRotate: 0,
    display: [
      { type: 'rond', checked: true },
      { type: 'arrondi carré', checked: false },
      { type: 'arrondi', checked: false },
      { type: 'pleine largeur', checked: false },
    ],
  },
];

export const getDefaultLinkDisplayOptions = displayType => {
  const base = deepCloneArray(DEFAULT_LINK_DISPLAY_OPTIONS);
  if (displayType === 'massonery') {
    const avatar = base.find(element => element.type === 'avatar');
    if (avatar?.display) {
      avatar.display.forEach(displayItem => {
        displayItem.checked = displayItem.type === 'pleine largeur';
      });
    }
  }
  return base;
};

export const mergeLinkDisplayOptions = (baseList, overrideList) => {
  if (!Array.isArray(overrideList) || overrideList.length === 0) {
    return deepCloneArray(baseList);
  }
  const combinedList = baseList.map(element => {
    const idx = overrideList.findIndex(
      elementFromGrid => elementFromGrid.type === element.type,
    );
    if (idx > -1) {
      if (element.type === 'avatar') {
        const display = element.display.map(displayElement => {
          const idxDisplay = overrideList[idx].display.findIndex(
            displayElementFromGrid =>
              displayElementFromGrid.type === displayElement.type,
          );
          if (idxDisplay > -1) {
            return overrideList[idx].display[idxDisplay];
          }
          return displayElement;
        });
        return { ...element, ...overrideList[idx], display };
      }
      if (element.type === 'secondary') {
        return normalizeSecondaryElement(overrideList[idx]);
      }
      return overrideList[idx];
    }
    return element;
  });
  return combinedList;
};

export const createDefaultSecondaryEntry = () => ({
  id: `secondary-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  checked: true,
  source: { kind: 'widget', widgetType: 'has_num', widgetId: null },
  position: 'bas',
  displayType: 'little',
  charLimit: 120,
  displayCase: 'case pleine largeur',
  bgColor: null,
  bgPicIndex: null,
  bgPicFit: 'content',
  bgMaskPicIndex: null,
  bgMaskFit: 'contain',
  bgMaskInvert: false,
  bgMaskRotate: 0,
});

export const normalizeSecondaryEntries = secondaryElement => {
  if (Array.isArray(secondaryElement?.entries)) {
    return secondaryElement.entries.map(entry => ({
      ...createDefaultSecondaryEntry(),
      ...entry,
      id: entry?.id || createDefaultSecondaryEntry().id,
      source: entry?.source || {
        kind: 'widget',
        widgetType: 'has_num',
        widgetId: null,
      },
    }));
  }
  if (
    secondaryElement &&
    (secondaryElement?.source || secondaryElement?.checked === true)
  ) {
    return [
      {
        ...createDefaultSecondaryEntry(),
        checked: secondaryElement?.checked === true,
        source: secondaryElement?.source || {
          kind: 'widget',
          widgetType: 'has_num',
          widgetId: null,
        },
        position: secondaryElement?.position || 'bas',
        displayType:
          secondaryElement?.displayType ||
          (secondaryElement?.source?.widgetType === 'text'
            ? 'truncate'
            : 'little'),
        charLimit:
          typeof secondaryElement?.charLimit === 'number'
            ? secondaryElement.charLimit
            : 120,
      },
    ];
  }
  return [];
};

export const normalizeSecondaryElement = secondaryElement => ({
  type: 'secondary',
  entries: normalizeSecondaryEntries(secondaryElement),
});

export const LinkDisplayOptionsEditor = props => {
  const {
    listToDisplay,
    setListToDisplay,
    maskOptions = null,
    widgetOptions = [],
  } = props;
  const labelElement = listToDisplay?.find(element => element.type === 'label');
  const avatarElement = listToDisplay?.find(
    element => element.type === 'avatar',
  );
  const avatarVariantInPreview =
    avatarElement?.display?.find(element => element.checked)?.type || 'rond';
  const labelVisibleInPreview = labelElement?.checked === true;
  const labelPositionInPreview = labelElement?.position || 'bas';
  const avatarVisibleInPreview = avatarElement?.checked === true;
  return (
    <>
      {listToDisplay?.map((element, idx) => {
        return (
          <ElementToDisplay
            key={idx}
            label={element.type}
            type={element.type}
            checked={element.checked}
            display={element.display}
            position={element.position}
            source={element.source}
            displayType={element.displayType}
            charLimit={element.charLimit}
            entries={element.entries}
            maskPicIndex={element.maskPicIndex}
            maskFit={element.maskFit}
            maskInvert={element.maskInvert}
            maskRotate={element.maskRotate}
            maskOptions={maskOptions}
            labelVisibleInPreview={labelVisibleInPreview}
            labelPositionInPreview={labelPositionInPreview}
            avatarVisibleInPreview={avatarVisibleInPreview}
            avatarVariantInPreview={avatarVariantInPreview}
            widgetOptions={widgetOptions}
            setListToDisplay={setListToDisplay}
          />
        );
      })}
    </>
  );
};

const ElementToDisplay = props => {
  const {
    label,
    type,
    checked,
    display,
    position,
    source,
    displayType,
    charLimit,
    entries,
    maskPicIndex,
    maskFit,
    maskInvert,
    maskRotate,
    maskOptions,
    labelVisibleInPreview,
    labelPositionInPreview,
    avatarVisibleInPreview,
    avatarVariantInPreview,
    widgetOptions,
    setListToDisplay,
  } = props;

  const genericMaskOptions = React.useMemo(
    () => [
      { key: 'none', label: 'Aucun masque' },
      { key: 0, label: 'Image 1' },
      { key: 1, label: 'Image 2' },
      { key: 2, label: 'Image 3' },
      { key: 3, label: 'Image 4' },
    ],
    [],
  );
  const resolvedMaskOptions =
    Array.isArray(maskOptions) && maskOptions.length > 0
      ? maskOptions
      : genericMaskOptions;

  const setElementState = nextState => {
    setListToDisplay(prev => {
      const clonedList = deepCloneArray(prev);
      const idx = clonedList.findIndex(element => element.type === type);
      if (idx === -1) {
        return clonedList;
      }
      clonedList[idx] = { ...clonedList[idx], ...nextState };
      return clonedList;
    });
  };

  const selectAvatarDisplay = nextType => {
    setListToDisplay(prev => {
      const clonedList = deepCloneArray(prev);
      const idx = clonedList.findIndex(element => element.type === type);
      if (idx === -1) {
        return clonedList;
      }
      clonedList[idx].checked = nextType !== 'masque';
      clonedList[idx].display = clonedList[idx].display.map(displayElement => ({
        ...displayElement,
        checked: displayElement.type === nextType,
      }));
      return clonedList;
    });
  };

  const normalizedEntries = normalizeSecondaryEntries({
    entries,
    source,
    checked,
    position,
    displayType,
    charLimit,
  });
  const widgetTypeOptions = React.useMemo(() => {
    const types = [];
    if (widgetOptions.some(option => option.source?.widgetType === 'text')) {
      types.push({ value: 'text', label: 'Texte' });
    }
    if (widgetOptions.some(option => option.source?.widgetType === 'has_num')) {
      types.push({ value: 'has_num', label: 'Nombre' });
    }
    if (
      widgetOptions.some(option => option.source?.widgetType === 'conditions')
    ) {
      types.push({ value: 'conditions', label: 'Bouton' });
    }
    return types;
  }, [widgetOptions]);
  const [draggedSecondaryId, setDraggedSecondaryId] = React.useState(null);
  const [dragOverSecondaryId, setDragOverSecondaryId] = React.useState(null);
  const [draggedSecondaryHeight, setDraggedSecondaryHeight] =
    React.useState(180);
  const dragImageRef = React.useRef(null);

  const updateSecondaryEntries = updater => {
    setListToDisplay(prev => {
      const clonedList = deepCloneArray(prev);
      const idx = clonedList.findIndex(element => element.type === type);
      if (idx === -1) {
        return clonedList;
      }
      const currentEntries = normalizeSecondaryEntries(clonedList[idx]);
      clonedList[idx] = {
        type: 'secondary',
        entries: updater(currentEntries),
      };
      return clonedList;
    });
  };

  const updateSecondaryEntry = (entryId, nextState) => {
    updateSecondaryEntries(currentEntries =>
      currentEntries.map(entry =>
        entry.id === entryId ? { ...entry, ...nextState } : entry,
      ),
    );
  };

  const moveSecondaryEntry = (draggedId, targetId) => {
    if (!draggedId || !targetId || draggedId === targetId) {
      return;
    }
    updateSecondaryEntries(currentEntries => {
      const draggedIndex = currentEntries.findIndex(
        entry => entry.id === draggedId,
      );
      const targetIndex = currentEntries.findIndex(
        entry => entry.id === targetId,
      );
      if (draggedIndex === -1 || targetIndex === -1) {
        return currentEntries;
      }
      const nextEntries = [...currentEntries];
      const [draggedEntry] = nextEntries.splice(draggedIndex, 1);
      nextEntries.splice(targetIndex, 0, draggedEntry);
      return nextEntries;
    });
  };

  return (
    <div className='my-05'>
      <div className='text-xs uppercase text-contrast text-opacity-60 mb-05'>
        {type === 'secondary' ? '' : label}
      </div>

      {type === 'label' && (
        <div className='flex flex-wrap gap-05'>
          {[
            { key: 'hidden', label: 'Masqué' },
            { key: 'haut', label: 'Haut' },
            { key: 'bas', label: 'Bas' },
            { key: 'gauche', label: 'Gauche' },
            { key: 'droite', label: 'Droite' },
            { key: 'overlay', label: 'Sur avatar' },
          ].map(option => {
            const isActive =
              option.key === 'hidden'
                ? checked !== true
                : checked === true && (position || 'bas') === option.key;
            return (
              <PreviewTile
                key={option.key}
                label={option.label}
                active={isActive}
                onClick={() =>
                  setElementState(
                    option.key === 'hidden'
                      ? { checked: false }
                      : { checked: true, position: option.key },
                  )
                }
              >
                <LabelPreview
                  variant={option.key}
                  showAvatar={avatarVisibleInPreview}
                  avatarVariant={avatarVariantInPreview}
                />
              </PreviewTile>
            );
          })}
        </div>
      )}

      {type === 'avatar' && (
        <div className='flex flex-col gap-05'>
          <div className='flex flex-wrap gap-05'>
            {[
              { key: 'masque', label: 'Masqué' },
              { key: 'rond', label: 'Rond' },
              { key: 'arrondi carré', label: 'Carré arrondi' },
              { key: 'arrondi', label: 'Carte' },
              { key: 'pleine largeur', label: 'Pleine largeur' },
            ].map(option => {
              const selectedType =
                display?.find(displayElement => displayElement.checked)?.type ||
                'rond';
              const isActive =
                option.key === 'masque'
                  ? checked !== true
                  : checked === true && selectedType === option.key;
              return (
                <PreviewTile
                  key={option.key}
                  label={option.label}
                  active={isActive}
                  onClick={() => {
                    if (option.key === 'masque') {
                      setElementState({ checked: false });
                      return;
                    }
                    selectAvatarDisplay(option.key);
                  }}
                >
                  <AvatarPreview
                    variant={option.key}
                    showLabel={labelVisibleInPreview}
                    labelPosition={labelPositionInPreview}
                  />
                </PreviewTile>
              );
            })}
          </div>

          <WidgetSettingsCollapsibleSection
            title='Masque avatar'
            defaultOpen={false}
            className='border-primary border-opacity-10'
          >
            <PicSelectionOptions
              title={null}
              options={resolvedMaskOptions}
              value={Number.isFinite(maskPicIndex) ? maskPicIndex : 'none'}
              size={28}
              onChange={value => {
                setElementState({
                  maskPicIndex: value === 'none' ? null : Number(value),
                });
              }}
              extra={
                <div className='flex flex-col gap-05'>
                  <MaskControls
                    fit={maskFit || 'contain'}
                    onFitChange={next => setElementState({ maskFit: next })}
                    invert={maskInvert === true}
                    onInvertChange={next => setElementState({ maskInvert: next })}
                    rotate={maskRotate ?? 0}
                    onRotateChange={next => setElementState({ maskRotate: next })}
                    includeFixed={true}
                    fillLabel="Remplir l'avatar"
                  />
                </div>
              }
            />
          </WidgetSettingsCollapsibleSection>
        </div>
      )}

      {type === 'secondary' && (
        <WidgetSettingsCollapsibleSection
          title='widgets additionnels'
          defaultOpen={false}
          className='border-primary border-opacity-10'
        >
          <div className='text-2xs text-contrast text-opacity-60'>
            Ajoute autant de widgets secondaires que nécessaire.
          </div>
          {normalizedEntries.map((entry, entryIdx) => {
            const entrySource = entry?.source;
            const currentWidgetType = entrySource?.widgetType || 'has_num';
            const widgetOptionsForType = widgetOptions.filter(
              option => option.source?.widgetType === currentWidgetType,
            );
            const entryDisplayOptions =
              entrySource?.widgetType === 'has_num'
                ? [
                    { value: 'big', label: 'Big' },
                    { value: 'big slash', label: 'Big slash' },
                    { value: 'little', label: 'Little' },
                    { value: 'jauge', label: 'Jauge' },
                    { value: 'jauge vertical', label: 'Jauge verticale' },
                    { value: 'little jauge', label: 'Little jauge' },
                    {
                      value: 'little jauge vertical',
                      label: 'Little jauge verticale',
                    },
                  ]
                : entrySource?.widgetType === 'conditions'
                  ? [
                      { value: 'big', label: 'Big' },
                      { value: 'little', label: 'Little' },
                      { value: 'rond', label: 'Rond' },
                      { value: 'checkbox', label: 'Checkbox' },
                    ]
                  : entrySource?.widgetType === 'text'
                    ? [{ value: 'truncate', label: 'Tronqué' }]
                    : [];
            const isDraggedEntry = draggedSecondaryId === entry.id;
            return (
              <React.Fragment key={entry.id}>
                {dragOverSecondaryId === entry.id &&
                  draggedSecondaryId &&
                  draggedSecondaryId !== entry.id && (
                    <div
                      className='rounded border-2 border-dashed border-secondary border-opacity-40 bg-secondary bg-opacity-5 flex items-center justify-center text-sm text-contrast text-opacity-45'
                      style={{ minHeight: draggedSecondaryHeight }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => {
                        e.preventDefault();
                        moveSecondaryEntry(draggedSecondaryId, entry.id);
                        setDraggedSecondaryId(null);
                        setDragOverSecondaryId(null);
                      }}
                    >
                      Déposer ici
                    </div>
                  )}
                <div
                  data-secondary-card='true'
                  onDragEnter={e => {
                    e.preventDefault();
                    if (draggedSecondaryId && draggedSecondaryId !== entry.id) {
                      setDragOverSecondaryId(entry.id);
                    }
                  }}
                  onDragOver={e => {
                    e.preventDefault();
                    if (draggedSecondaryId && draggedSecondaryId !== entry.id) {
                      setDragOverSecondaryId(entry.id);
                    }
                  }}
                  onDrop={e => {
                    e.preventDefault();
                    moveSecondaryEntry(draggedSecondaryId, entry.id);
                    setDraggedSecondaryId(null);
                    setDragOverSecondaryId(null);
                  }}
                  onDragEnd={() => {
                    setDraggedSecondaryId(null);
                    setDragOverSecondaryId(null);
                    if (dragImageRef.current?.parentNode) {
                      dragImageRef.current.parentNode.removeChild(
                        dragImageRef.current,
                      );
                    }
                    dragImageRef.current = null;
                  }}
                  className={`rounded border border-primary border-opacity-10 bg-main bg-opacity-30 transition ${
                    dragOverSecondaryId === entry.id &&
                    draggedSecondaryId !== entry.id
                      ? 'border-secondary bg-secondary bg-opacity-10'
                      : ''
                  } ${isDraggedEntry ? 'opacity-45' : ''}`}
                >
                  <WidgetSettingsCollapsibleSection
                    title={`Champ secondaire ${entryIdx + 1}`}
                    defaultOpen={entryIdx === 0}
                    className='border-0 bg-transparent'
                  >
                    <div className='flex flex-col gap-05'>
                      <div className='flex items-center gap-05'>
                        <div
                          className='cursor-grab text-xs text-contrast text-opacity-45 select-none'
                          draggable
                          onDragStart={e => {
                            const card = e.currentTarget.closest(
                              '[data-secondary-card="true"]',
                            );
                            const rect = card?.getBoundingClientRect?.();
                            const height = rect?.height || 0;
                            if (height > 0) {
                              setDraggedSecondaryHeight(height);
                            }
                            setDraggedSecondaryId(entry.id);
                            setDragOverSecondaryId(null);
                            const clone = card?.cloneNode(true);
                            if (clone && rect) {
                              clone.style.position = 'fixed';
                              clone.style.top = '-9999px';
                              clone.style.left = '-9999px';
                              clone.style.width = `${rect.width}px`;
                              clone.style.pointerEvents = 'none';
                              clone.style.opacity = '0.9';
                              clone.style.transform = 'rotate(1deg)';
                              clone.style.boxShadow =
                                '0 12px 24px rgba(0,0,0,0.35)';
                              clone.style.zIndex = '9999';
                              document.body.appendChild(clone);
                              dragImageRef.current = clone;
                              if (e.dataTransfer) {
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('text/plain', entry.id);
                                e.dataTransfer.setDragImage(
                                  clone,
                                  Math.min(24, rect.width / 2),
                                  20,
                                );
                              }
                            }
                          }}
                          onDragEnd={() => {
                            setDraggedSecondaryId(null);
                            setDragOverSecondaryId(null);
                            if (dragImageRef.current?.parentNode) {
                              dragImageRef.current.parentNode.removeChild(
                                dragImageRef.current,
                              );
                            }
                            dragImageRef.current = null;
                          }}
                        >
                          drag
                        </div>
                        <div className='flex-1 text-sm text-contrast text-opacity-80'>
                          Champ secondaire {entryIdx + 1}
                        </div>
                        <Checkbox
                          label='Afficher'
                          value={entry.checked === true}
                          onChange={next =>
                            updateSecondaryEntry(entry.id, { checked: next })
                          }
                        />
                        <button
                          type='button'
                          className='text-xs px-03 py-02 rounded bg-main text-contrast text-opacity-80 hover:opacity-70'
                          onClick={() =>
                            updateSecondaryEntries(currentEntries =>
                              currentEntries.filter(
                                current => current.id !== entry.id,
                              ),
                            )
                          }
                        >
                          Supprimer
                        </button>
                      </div>
                      <SettingsRow label='Champ'>
                        <div className='flex gap-05 w-full'>
                          <SettingsSelect
                            value={currentWidgetType}
                            onChange={e => {
                              const nextWidgetType = e.target.value;
                              const firstOptionForType = widgetOptions.find(
                                option =>
                                  option.source?.widgetType === nextWidgetType,
                              );
                              updateSecondaryEntry(entry.id, {
                                source: firstOptionForType?.source || {
                                  kind: 'widget',
                                  widgetType: nextWidgetType,
                                  widgetId: null,
                                },
                                displayType:
                                  nextWidgetType === 'conditions'
                                    ? 'little'
                                    : nextWidgetType === 'text'
                                      ? 'truncate'
                                      : 'little',
                              });
                            }}
                          >
                            {widgetTypeOptions.map(option => (
                              <option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </option>
                            ))}
                          </SettingsSelect>
                          <SettingsSelect
                            value={entrySource?.widgetId || ''}
                            onChange={e => {
                              const nextOption = widgetOptions.find(
                                option => option.value === e.target.value,
                              );
                              updateSecondaryEntry(entry.id, {
                                source: nextOption?.source || {
                                  kind: 'widget',
                                  widgetType: currentWidgetType,
                                  widgetId: null,
                                },
                                displayType:
                                  nextOption?.source?.widgetType ===
                                  'conditions'
                                    ? 'little'
                                    : nextOption?.source?.widgetType === 'text'
                                      ? 'truncate'
                                      : 'little',
                              });
                            }}
                          >
                            <option value=''>Aucun widget</option>
                            {widgetOptionsForType.map(option => (
                              <option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </option>
                            ))}
                          </SettingsSelect>
                        </div>
                      </SettingsRow>
                      <SettingsRow label='Affichage'>
                        <SettingsSelect
                          value={
                            entry.displayType ||
                            (entrySource?.widgetType === 'text'
                              ? 'truncate'
                              : 'little')
                          }
                          onChange={e =>
                            updateSecondaryEntry(entry.id, {
                              displayType: e.target.value,
                            })
                          }
                        >
                          {entryDisplayOptions.map(option => (
                            <option
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </option>
                          ))}
                        </SettingsSelect>
                      </SettingsRow>
                      <SettingsRow label='Position'>
                        <SettingsSelect
                          value={entry.position || 'bas'}
                          onChange={e =>
                            updateSecondaryEntry(entry.id, {
                              position: e.target.value,
                            })
                          }
                        >
                          <option value='haut'>Haut</option>
                          <option value='bas'>Bas</option>
                          <option value='gauche'>Gauche</option>
                          <option value='droite'>Droite</option>
                        </SettingsSelect>
                      </SettingsRow>
                      {entrySource?.widgetType === 'text' && (
                        <SettingsRow label='Nb caractères'>
                          <SettingsSelect
                            value={String(entry.charLimit || 120)}
                            onChange={e =>
                              updateSecondaryEntry(entry.id, {
                                charLimit: Number(e.target.value),
                              })
                            }
                          >
                            <option value='40'>40</option>
                            <option value='80'>80</option>
                            <option value='120'>120</option>
                            <option value='200'>200</option>
                            <option value='400'>400</option>
                          </SettingsSelect>
                        </SettingsRow>
                      )}
                      <WidgetSettingsCollapsibleSection
                        title='Style de case'
                        defaultOpen={false}
                        className='border-primary border-opacity-10'
                      >
                        <SettingsRow label='Case'>
                          <SettingsSelect
                            value={entry?.displayCase || 'case pleine largeur'}
                            onChange={e =>
                              updateSecondaryEntry(entry.id, {
                                displayCase: e.target.value,
                              })
                            }
                          >
                            <option value='case pleine largeur'>
                              Case pleine largeur
                            </option>
                            <option value='case arrondie'>Case arrondie</option>
                            <option value='masque'>Masque</option>
                          </SettingsSelect>
                        </SettingsRow>
                        <SettingsRow label='Couleur fond' dense>
                          <ColorsSwatch
                            currentColor={entry?.bgColor || 'transparent'}
                            colors={[
                              'transparent',
                              'primary',
                              'secondary-d',
                              'red',
                              'orange',
                              'yellow',
                              'lime',
                              'green',
                              'blue',
                              'purple',
                              'pink',
                            ]}
                            onClick={color =>
                              updateSecondaryEntry(entry.id, {
                                bgColor:
                                  color === 'transparent' ? null : color,
                              })
                            }
                          />
                        </SettingsRow>
                        <PicSelectionOptions
                          title='Image de fond'
                          options={resolvedMaskOptions}
                          value={
                            Number.isFinite(entry?.bgPicIndex)
                              ? entry.bgPicIndex
                              : 'none'
                          }
                          size={28}
                          onChange={value =>
                            updateSecondaryEntry(entry.id, {
                              bgPicIndex:
                                value === 'none' ? null : Number(value),
                            })
                          }
                        />
                        <SettingsRow label='Affichage fond'>
                          <FitOptionsButtons
                            value={entry?.bgPicFit || 'content'}
                            includeFixed={false}
                            includeContent={true}
                            fillLabel='Remplir la case'
                            onChange={next =>
                              updateSecondaryEntry(entry.id, {
                                bgPicFit: next,
                              })
                            }
                          />
                        </SettingsRow>
                        {entry?.displayCase === 'masque' && (
                          <>
                            <PicSelectionOptions
                              title='Image de masque'
                              options={resolvedMaskOptions}
                              value={
                                Number.isFinite(entry?.bgMaskPicIndex)
                                  ? entry.bgMaskPicIndex
                                  : 'none'
                              }
                              size={28}
                              onChange={value =>
                                updateSecondaryEntry(entry.id, {
                                  bgMaskPicIndex:
                                    value === 'none' ? null : Number(value),
                                })
                              }
                            />
                            <MaskControls
                              fit={entry?.bgMaskFit || 'contain'}
                              onFitChange={next =>
                                updateSecondaryEntry(entry.id, {
                                  bgMaskFit: next,
                                })
                              }
                              invert={entry?.bgMaskInvert === true}
                              onInvertChange={value =>
                                updateSecondaryEntry(entry.id, {
                                  bgMaskInvert: value,
                                })
                              }
                              rotate={entry?.bgMaskRotate ?? 0}
                              onRotateChange={next =>
                                updateSecondaryEntry(entry.id, {
                                  bgMaskRotate: next,
                                })
                              }
                              includeFixed={true}
                            />
                          </>
                        )}
                      </WidgetSettingsCollapsibleSection>
                    </div>
                  </WidgetSettingsCollapsibleSection>
                </div>
              </React.Fragment>
            );
          })}
          <button
            type='button'
            className='text-sm px-05 py-03 rounded bg-primary text-main hover:opacity-80 self-start'
            onClick={() =>
              updateSecondaryEntries(currentEntries => [
                ...currentEntries,
                createDefaultSecondaryEntry(),
              ])
            }
          >
            Ajouter un champ secondaire
          </button>
        </WidgetSettingsCollapsibleSection>
      )}
    </div>
  );
};

export const buildSecondaryWidgetOptions = item => {
  if (!item) return [];
  const options = [];

  (item.text || []).forEach(textObj => {
    const id = getId(textObj);
    const payload = textObj?.[id] || {};
    options.push({
      value: id,
      label: `Texte - ${payload?.label || payload?.text?.slice?.(0, 24) || id}`,
      source: { kind: 'widget', widgetType: 'text', widgetId: id },
    });
  });

  (item.has_num || []).forEach(numObj => {
    const id = getId(numObj);
    const payload = numObj?.[id] || {};
    options.push({
      value: id,
      label: `Nombre - ${payload?.label || id}`,
      source: { kind: 'widget', widgetType: 'has_num', widgetId: id },
    });
  });

  (item.conditions || []).forEach(conditionObj => {
    const id = getId(conditionObj);
    const payload = conditionObj?.[id] || {};
    const activeLabel =
      payload?.active?.label ||
      payload?.inactive?.label ||
      payload?.label ||
      id;
    options.push({
      value: id,
      label: `Bouton - ${activeLabel}`,
      source: { kind: 'widget', widgetType: 'conditions', widgetId: id },
    });
  });

  return options;
};

const PreviewTile = props => {
  const { active, label, onClick, children } = props;
  return (
    <button
      type='button'
      onClick={onClick}
      className={`w-88 rounded border p-05 flex flex-col gap-05 items-center transition ${
        active
          ? 'border-secondary bg-secondary bg-opacity-20'
          : 'border-primary border-opacity-20 hover:bg-main hover:bg-opacity-50'
      }`}
    >
      <div className='w-full h-58 rounded bg-primary-d bg-opacity-50 flex items-center justify-center overflow-hidden relative'>
        {children}
      </div>
      <div className='text-xs leading-3 text-center text-contrast text-opacity-80'>
        {label}
      </div>
    </button>
  );
};

const LabelPreview = ({
  variant,
  showAvatar = true,
  avatarVariant = 'rond',
}) => {
  const showOverlayLabel =
    variant === 'overlay' && showAvatar && avatarVariant === 'pleine largeur';
  const bottomLabelClassName =
    variant === 'overlay'
      ? 'bg-main bg-opacity-70'
      : 'bg-contrast bg-opacity-50';
  const avatarClassName =
    avatarVariant === 'rond'
      ? 'w-2 h-2 rounded-full'
      : avatarVariant === 'arrondi carré'
        ? 'w-2 h-2 rounded'
        : avatarVariant === 'arrondi'
          ? 'w-full h-full rounded m-05'
          : avatarVariant === 'pleine largeur'
            ? 'w-full h-full'
            : 'w-full h-full rounded';

  return (
    <div className='w-full h-full rounded border border-primary border-opacity-10 flex flex-col gap-05 justify-center overflow-hidden'>
      {variant === 'gauche' || variant === 'droite' ? (
        <div className='flex-1 flex items-center gap-05'>
          {variant === 'gauche' && variant !== 'hidden' && (
            <div className='flex-1 flex items-center justify-center'>
              <div
                className={`flex-1 h-px10 rounded-full ${bottomLabelClassName}`}
              />
            </div>
          )}
          {showAvatar && (
            <div className='flex-1 h-full flex items-center justify-center relative'>
              <div className={`${avatarClassName} bg-primary bg-opacity-35`} />
            </div>
          )}
          {variant === 'droite' && variant !== 'hidden' && (
            <div className='flex-1 flex items-center justify-center'>
              <div
                className={`flex-1 h-px10 rounded-full ${bottomLabelClassName}`}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          {variant === 'haut' && variant !== 'hidden' && (
            <div className='flex-1 max-h-px10 rounded-full bg-contrast bg-opacity-50' />
          )}
          {showAvatar && (
            <div className='flex-3 flex items-center justify-center relative'>
              <div className={`${avatarClassName} bg-primary bg-opacity-35`} />
              {showOverlayLabel && (
                <div className='absolute left-0 right-0 bottom-0 h-px10 rounded bg-main bg-opacity-70' />
              )}
            </div>
          )}
          {variant !== 'hidden' && variant !== 'haut' && !showOverlayLabel && (
            <div
              className={`flex-1 max-h-px10 rounded-full ${bottomLabelClassName}`}
            />
          )}
        </>
      )}
    </div>
  );
};

const AvatarPreview = ({
  variant,
  showLabel = true,
  labelPosition = 'bas',
}) => {
  const showOverlayLabel =
    showLabel && labelPosition === 'overlay' && variant === 'pleine largeur';
  const bottomLabelClassName =
    labelPosition === 'overlay'
      ? 'bg-main bg-opacity-70'
      : 'bg-contrast bg-opacity-50';

  return (
    <div className='w-full h-full rounded border border-primary border-opacity-10 flex flex-col gap-05 justify-center overflow-hidden'>
      {labelPosition === 'gauche' || labelPosition === 'droite' ? (
        <div className='flex-1 flex items-center gap-05'>
          {showLabel && labelPosition === 'gauche' && (
            <div className='flex-1 flex items-center justify-center'>
              <div
                className={`flex-1 h-px10 rounded-full ${bottomLabelClassName}`}
              />
            </div>
          )}
          <div className='flex-1 h-full flex items-center justify-center relative'>
            {variant !== 'masque' && (
              <div
                className={`bg-primary bg-opacity-35 ${
                  variant === 'rond'
                    ? 'w-2 h-2 rounded-full'
                    : variant === 'arrondi carré'
                      ? 'w-2 h-2 rounded'
                      : variant === 'arrondi'
                        ? 'w-full h-full rounded m-05'
                        : 'w-full h-full'
                }`}
              />
            )}
          </div>
          {showLabel && labelPosition === 'droite' && (
            <div className='flex-1 flex items-center justify-center'>
              <div
                className={`flex-1 h-px10 rounded-full ${bottomLabelClassName}`}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          {showLabel && labelPosition === 'haut' && (
            <div className='flex-1 max-h-px10 rounded-full bg-contrast bg-opacity-50' />
          )}
          <div className='flex-3 flex items-center justify-center relative'>
            {variant !== 'masque' && (
              <div
                className={`bg-primary bg-opacity-35 ${
                  variant === 'rond'
                    ? 'w-2 h-2 rounded-full'
                    : variant === 'arrondi carré'
                      ? 'w-2 h-2 rounded'
                      : variant === 'arrondi'
                        ? 'w-full h-full rounded m-05'
                        : 'w-full h-full'
                }`}
              />
            )}
            {showOverlayLabel && (
              <div className='absolute left-0 right-0 bottom-0 h-px10 rounded bg-main bg-opacity-70' />
            )}
          </div>
          {showLabel && labelPosition !== 'haut' && !showOverlayLabel && (
            <div
              className={`flex-1 max-h-px10 rounded-full ${bottomLabelClassName}`}
            />
          )}
        </>
      )}
    </div>
  );
};
